import { useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  API_FIELDS,
  RECON_STATUS_OPTIONS,
} from './issueTypes';
import { BASE_COLUMNS } from './issueColumns';

export function IssuesDataGrid({
  rows,
  setRows,
  loading,
  rowCountState,
  paginationModel,
  onPaginationModelChange,
  pendingIgnoreIds,
  onIgnoreClick,
}) {
  const columnsWithActions = useMemo(() => {
    const reconStatusColumn = {
      field: API_FIELDS.RECON_STATUS,
      headerName: 'Recon Status',
      width: 160,
      flex: 0,
      renderCell: (params) => {
        const row = params.row;
        const value =
          row.RECON_STATUS != null && String(row.RECON_STATUS).trim() !== ''
            ? String(row.RECON_STATUS)
            : '';
        const reconId = row.RECON_ID;
        return (
          <select
            value={value}
            onChange={(e) => {
              const newVal = e.target.value;
              setRows((prev) =>
                prev.map((r) =>
                  r.RECON_ID === reconId ? { ...r, RECON_STATUS: newVal || null } : r
                )
              );
            }}
            style={{ width: '100%', minWidth: '130px', padding: '4px 6px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '3px', height: 'auto', overflow: 'visible' }}
          >
            <option value="">Select</option>
            {RECON_STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      },
    };
    const baseColumns = BASE_COLUMNS.map((c) =>
      c.field === API_FIELDS.RECON_STATUS ? reconStatusColumn : c
    );
    return [
      ...baseColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        flex: 0,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row;
          const reconId = row.RECON_ID;
          if (reconId == null) return null;
          const id = Number(reconId);
          const isPending =
            (typeof row.PENDING_APPROVAL === 'string' && row.PENDING_APPROVAL === 'PENDING') ||
            pendingIgnoreIds.has(id);
          return (
            <button
              type="button"
              disabled={isPending}
              onClick={() => onIgnoreClick(row)}
              className={isPending ? 'btn-blue' : 'btn-blue'}
              style={{
                padding: '3px 10px',
                height: '26px',
                fontSize: '11px',
                opacity: isPending ? 0.5 : 1,
                cursor: isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {isPending ? 'Pending' : 'Ignore'}
            </button>
          );
        },
      },
    ];
  }, [onIgnoreClick, pendingIgnoreIds, setRows]);

  return (
    <article className="card">
      <header>
        <i className="fa fa-table"></i>
        <label>Issues ({rowCountState})</label>
      </header>
      <div className="body" style={{ padding: 0, minHeight: 400 }}>
        <DataGrid
          columns={columnsWithActions}
          rows={rows}
          getRowId={(row) => row.RECON_ID ?? 0}
          paginationMode="server"
          rowCount={rowCountState}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight
          hideFooter
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #0098DB',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '11px',
              fontWeight: 700,
              color: '#0039A6',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              whiteSpace: 'normal',
              lineHeight: '1.3',
              textAlign: 'left',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '13px',
              color: '#333',
              borderBottom: '1px solid #eee',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f0f7ff',
            },
          }}
        />
        {!loading && rowCountState > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #eee', fontSize: '13px', color: '#666' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Rows per page:</span>
              <select
                value={paginationModel.pageSize}
                onChange={(e) => onPaginationModelChange({ page: 0, pageSize: Number(e.target.value) })}
                style={{ height: '30px', padding: '2px 6px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '3px', overflow: 'visible' }}
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>
                {paginationModel.page * paginationModel.pageSize + 1}–{Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCountState)} of {rowCountState}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-blue" disabled={paginationModel.page <= 0} onClick={() => onPaginationModelChange({ ...paginationModel, page: paginationModel.page - 1 })} style={{ padding: '4px 14px', height: '30px', fontSize: '12px', opacity: paginationModel.page <= 0 ? 0.5 : 1 }}>Previous</button>
              <button className="btn-blue" disabled={(paginationModel.page + 1) * paginationModel.pageSize >= rowCountState} onClick={() => onPaginationModelChange({ ...paginationModel, page: paginationModel.page + 1 })} style={{ padding: '4px 14px', height: '30px', fontSize: '12px', opacity: (paginationModel.page + 1) * paginationModel.pageSize >= rowCountState ? 0.5 : 1 }}>Next</button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
